/**
 * Modal 관련 기능 구현
 *   - Modal Open
 *   - 확인 버튼 클릭시 처리
 *   - Tab : Palette / Split / X축 데이터 선택 / Y축 Primary Secondary 선택
 */

import BriqueChart from './BriqueChart.vue'
import { ToastProgrammatic as Toast } from 'buefy'
import { _ } from 'core-js'

export default {
  /**
   * 모달 대화 상자를 open하고 데이터를 세팅한다.
   * @param {Object} element Chart Element
   * @param {String} activeStrTabStep Active할 tabStep
   */
  openChartModal (element, activeStrTabStep) {
    element.parentElement.__vue__.chartModal.chartData = element.briqueChartData.tracesOriginal
    element.parentElement.__vue__.chartModal.chartTitle = element.layout.title?.text ?? element.parentElement.__vue__.$t('fdc.CHART_COMPONENT.LABEL.DEFAULT_CHART_TITLE')
    element.parentElement.__vue__.chartModal.visible = true

    element.parentElement.__vue__.chartModal.tabStep = activeStrTabStep
  },

  /**
   * 차트 모달에서 확인 버튼 클릭시 처리
   * @param {Object} returnObject returnObject.visible을 true로 하면 모달창이 닫히지 않는다.
   * @param {Object} chartVue Chart Vuew Object
   * @param {Object} modalData 차트 모달에서 설정한 데이터
   * @param {Object} oldModalData 이전에 차트 모달에서 설정한 데이터
   */
  onClickConfirmButtonInModal (returnObject, chartVue, modalData, oldModalData) {
    if (_.isEqual(modalData, oldModalData)) return

    BriqueChart.applyPalette(chartVue, modalData, oldModalData)
    BriqueChart.applySplitChart(chartVue, modalData, oldModalData)
    BriqueChart.applyXAxisDataSelection(chartVue, modalData, oldModalData)
    BriqueChart.applySeriesYAxis(chartVue, modalData, oldModalData)
  },

  /**
   * Palette 변경 사항 적용
   * @param {Object} chartVue Chart Vuew Object
   * @param {Object} modalData 변경된 Modal 데이터
   * @param {Object} oldModalData 변경되기전의 Modal 데이터
   * @returns
   */
  applyPalette (chartVue, modalData, oldModalData) {
    if (_.isEqual(modalData.palette, oldModalData.palette)) { // palette 설정을 변경하지 않았으면
      return
    }

    let element = chartVue.$el.children[0]
    // let layout = {}
    // let colorwayLayout = BriqueChart.applyPaletteToLayout(element, element.layout, modalData.palette.name, true)
    // _.merge(layout, colorwayLayout)

    // if (!_.isEmpty(layout)) {
    //   BriqueChart.relayout(element, layout, true)
    // }

    BriqueChart.applyPaletteToLayout(element, element.layout, modalData.palette.name, true)

    Toast.open({
      message: chartVue.$t('fdc.CHART_COMPONENT.MESSAGE.INFORM_CHANGE_PALETTE'),
      type: 'is-success',
      queue: false // queue: false => toast가 2개 이상일 때 같은 영역에 덮어쓰지 않고, 최신것이 위에 표시되게 하는 방식
    })
  },

  /**
   * Split 변경 사항 적용
   * @param {Object} chartVue Chart Vuew Object
   * @param {Object} modalData 변경된 Modal 데이터
   * @param {Object} oldModalData 변경되기전의 Modal 데이터
   * @returns
   */
  applySplitChart (chartVue, modalData, oldModalData) {
    if (_.isEqual(modalData.splits, oldModalData.splits)) { // 스플릿 차트 설정이 변경되지 않았으면
      return
    }

    let element = chartVue.$el.children[0]
    chartVue.splitCharts.splice(0, chartVue.splitCharts.length)

    for (let i = 0; i < modalData.splits.length; i++) {
      let splitChart = modalData.splits[i]
      let checkedSeries = splitChart.seriesList.filter(item => item.checked)

      if (_.isEmpty(checkedSeries)) continue // 체크된것이 없으면 Skip

      let id = splitChart.id
      chartVue.splitCharts.push({ id })

      let data = []
      for (let trace of element.data) {
        if (checkedSeries.find((item) => item.name === trace.name)) {
          data.push(trace)
        }
      }

      setTimeout(() => {
        let layout = _.merge({}, element.briqueChartData.layoutOriginal)
        layout.title = splitChart.title + ' ' + (i + 1)
        // layout.xaxis.rangeslider = undefined// range view는 default로 안나오게
        BriqueChart.newPlot(id, data, layout, element.briqueChartData.configOriginal, element.briqueChartOption)
      })
    }

    if (_.isEmpty(chartVue.splitCharts)) {
      element.parentElement.style.maxHeight = ''
      window.dispatchEvent(new Event('resize'))
    } else {
      element.parentElement.style.maxHeight = '74%'
      window.dispatchEvent(new Event('resize'))
    }

    Toast.open({
      message: chartVue.$t('fdc.CHART_COMPONENT.MESSAGE.INFORM_MAKE_SPLIT_CHART'),
      type: 'is-success',
      queue: false // queue: false => toast가 2개 이상일 때 같은 영역에 덮어쓰지 않고, 최신것이 위에 표시되게 하는 방식
    })
  },

  /**
   * X축 데이터 선택 변경사항 적용
   * @param {Object} chartVue Chart Vuew Object
   * @param {Object} modalData 변경된 Modal 데이터
   * @param {Object} oldModalData 변경되기전의 Modal 데이터
   * @returns
   */
  applyXAxisDataSelection (chartVue, modalData, oldModalData) {
    if (_.isEqual(modalData.xAxisDataSelectionValue, oldModalData.xAxisDataSelectionValue)) { // X축 데이터 선택 설정이 변경되지 않았으면
      return
    }

    if (_.isEmpty(modalData.xAxisDataSelectionValue)) {
      return
    }

    let element = chartVue.$el.children[0]
    let ori = element.briqueChartData.tracesOriginal

    let layout = _.merge({}, element.layout, {
      xaxis: {
        type: '',
        range: []
      }
    })

    element.briqueChartData.isDataNormalization = false // 정규화 해제
    BriqueChart.restyleModeBarButtons(element)

    if (modalData.xAxisDataSelectionValue === 'index') {
      element.briqueChartData.isXAsixDataSelection = true

      _.merge(layout, {
        xaxis: {
          title: {
            text: 'Index'
          }
        }
      })

      let data = _.cloneDeep(ori)
      let xList = new Array(data[0].x.length).fill(0).map((item, index) => index + 1)

      data.map((trace, i) => {
        trace.x = xList
        element.briqueChartData.setSeriesStyle(trace.name, { mode: element.briqueChartData.tracesOriginal[i].mode })
      })

      layout.xaxis.range = [] // X축 범위 초기화
      element.layout.xaxis.range = [] // X축 범위 초기화

      layout.yaxis.range = [] // Y축 범위 초기화
      element.layout.yaxis.range = [] // Y축 범위 초기화

      BriqueChart.setXMinMax(element, data, layout, true)
      BriqueChart.setYMinMax(element, data, layout)

      element.briqueChartData.tracesOriginalAfterXAxisDataSelection = _.cloneDeep(data)
      if (element.briqueChartData.isSampling) {
        data = BriqueChart.getSamplingData(element).traces
      }

      BriqueChart.react(element, data, layout)
    } else if (modalData.xAxisDataSelectionValue === 'defaultXAxisData') { // 원본 데이터
      element.briqueChartData.isXAsixDataSelection = false
      layout.xaxis = _.cloneDeep(element.briqueChartData.layoutOriginal.xaxis)
      element.layout.xaxis = _.cloneDeep(element.briqueChartData.layoutOriginal.xaxis)

      layout.yaxis = _.cloneDeep(element.briqueChartData.layoutOriginal.yaxis)
      element.layout.yaxis = _.cloneDeep(element.briqueChartData.layoutOriginal.yaxis)

      element.briqueChartData.tracesOriginalAfterXAxisDataSelection = _.cloneDeep(ori)
      let data = _.cloneDeep(ori)

      data.map((trace, i) => {
        element.briqueChartData.setSeriesStyle(trace.name, { mode: element.briqueChartData.tracesOriginal[i].mode })
      })

      BriqueChart.setXMinMax(element, data, layout, false)
      BriqueChart.setYMinMax(element, data, layout)

      if (element.briqueChartData.isSampling) {
        data = BriqueChart.getSamplingData(element).traces
      }

      BriqueChart.react(element, data, layout)
    } else { // 시리즈에서 선택시
      element.briqueChartData.isXAsixDataSelection = true

      let data = _.cloneDeep(ori)
      let index = data.findIndex(item => item.name === modalData.xAxisDataSelectionValue)
      let selectedTrace = data[index]

      data.splice(index, 1)
      data.map((trace, i) => {
        trace.x = selectedTrace.y
        element.briqueChartData.setSeriesStyle(trace.name, { mode: 'markers' })
      })

      _.merge(layout, {
        xaxis: {
          title: {
            text: selectedTrace.name
          }
        }
      })

      layout.xaxis.range = [] // X축 범위 초기화
      element.layout.xaxis.range = [] // X축 범위 초기화

      layout.yaxis.range = [] // Y축 범위 초기화
      element.layout.yaxis.range = [] // Y축 범위 초기화

      BriqueChart.setXMinMax(element, data, layout, true)
      BriqueChart.setYMinMax(element, data, layout)

      element.briqueChartData.tracesOriginalAfterXAxisDataSelection = _.cloneDeep(data)
      if (element.briqueChartData.isSampling) {
        data = BriqueChart.getSamplingData(element).traces
      }

      BriqueChart.react(element, data, layout)
    }
    Toast.open({
      message: chartVue.$t('fdc.CHART_COMPONENT.MESSAGE.INFORM_CHANGE_X_AXIS_DATA'),
      type: 'is-success',
      queue: false // queue: false => toast가 2개 이상일 때 같은 영역에 덮어쓰지 않고, 최신것이 위에 표시되게 하는 방식
    })
  },

  /**
   * Y축 Primary / Secondary 선택 변경사항 적용
   * @param {Object} chartVue Chart Vuew Object
   * @param {Object} modalData 변경된 Modal 데이터
   * @param {Object} oldModalData 변경되기전의 Modal 데이터
   * @returns
   */
  applySeriesYAxis (chartVue, modalData, oldModalData) {
    if (_.isEqual(modalData.seriesYAxisNameList, oldModalData.seriesYAxisNameList)) { // Y축 Primary / Secondary 선택 설정이 변경되지 않았으면
      return
    }

    let element = chartVue.$el.children[0]
    modalData.seriesYAxisNameList.map((yaxisName, index) => {
      // element.briqueChartData.seriesStyles[index] = element.briqueChartData.seriesStyles[index] ?? {}
      // element.briqueChartData.seriesStyles[index].yaxis = yaxisName // y, y2
      element.briqueChartData.setSeriesStyle(index, { yaxis: yaxisName }) // y, y2
    })

    BriqueChart.react(element, element.data)
  },

  /**
   * 모달 대화 상자를 open하고 데이터를 세팅한다.
   * @param {Object} element Chart Element
   */
  openChartModalDataGrid (element) {
    element.parentElement.__vue__.chartModalDataGrid.chartData = element.briqueChartData.tracesOriginal
    element.parentElement.__vue__.chartModalDataGrid.chartTitle = element.layout.title?.text ?? element.parentElement.__vue__.$t('fdc.CHART_COMPONENT.LABEL.DEFAULT_CHART_TITLE')
    element.parentElement.__vue__.chartModalDataGrid.visible = true
  }
}
